import {FaPen} from "react-icons/fa";
import FormInput from "../../components/common/FormInput";
import CustomButton from "../../components/common/CustomButton";
import React, {useEffect, useRef, useState} from "react";
import toast from "react-hot-toast";
import {useUser} from "@clerk/clerk-react";
import {useResourcesService} from "../../services";
import {useSelector} from "react-redux";
import {useJwt} from "../../helpers/common";
import { useResourceInfo } from "../../hooks/useResourceInfo";

interface UserProfileProps {
  profile: any;
  loading: boolean;
  onUpdate: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ profile, loading, onUpdate }) => {
  const { user, isLoaded } = useUser();
  const { updateUserProfile } = useResourcesService();
  const jwtData: any = useJwt();
  const roleLabel = (typeof jwtData?.org_role === 'string' && jwtData.org_role.toLowerCase() === 'admin') ? 'Admin' : 'Member';
  
  useResourceInfo();

  const [isEditingProfile, setIsEditingProfile] = useState<boolean>(false);
  const [skills, setSkills] = useState<any[]>([]);
  const [firstNameInput, setFirstNameInput] = useState<string>('');
  const [lastNameInput, setLastNameInput] = useState<string>('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [updatingProfile, setUpdatingProfile] = useState<boolean>(false);
  const [profileSkillIds, setProfileSkillIds] = useState<string[]>([]);
  const [enableUserMemoryStorage, setEnableUserMemoryStorage] = useState<boolean>(false);
  const [referenceUserChatHistory, setReferenceUserChatHistory] = useState<boolean>(false);

  const {
    skills: orgSkills = [],
  } = useSelector((state: any) => state.organization);

  const handleAvatarClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleAvatarFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !user) return;

    try {
      setAvatarUploading(true);
      
      // Strip metadata by redrawing on canvas
      const strippedBlob = await new Promise<Blob>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject(new Error('Failed to get canvas context'));
            ctx.drawImage(img, 0, 0);
            canvas.toBlob((blob) => {
              if (blob) resolve(blob);
              else reject(new Error('Failed to create blob'));
            }, file.type || 'image/jpeg');
          };
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = event.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });

      const updated = await user.setProfileImage({ file: strippedBlob as any });
      const newUrl = updated?.publicUrl;
      setAvatarUrl(newUrl);

      if(newUrl) {
        await updateUserProfile({ image_url: newUrl });
        setTimeout(()=>{

          window.location.reload();
        }, 3000);
      }
    } catch (err: any) {
      console.error('Avatar upload error:', err);
      toast.error('Failed to update profile image');
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveProfile = async () => {
    if (!firstNameInput.trim()) {
      toast.error('First name cannot be empty');
      return;
    }
    if (!lastNameInput.trim()) {
      toast.error('Last name cannot be empty');
      return;
    }

    setUpdatingProfile(true);
    try {
      await updateUserProfile({
        first_name: firstNameInput,
        last_name: lastNameInput,
        enable_user_memory_storage: enableUserMemoryStorage,
        reference_user_chat_history: referenceUserChatHistory
      });
      setIsEditingProfile(false);
      if(onUpdate) onUpdate();
      window.location.reload();
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setUpdatingProfile(false);
    }
  };

  useEffect(() => {
    if(orgSkills.length === 0) return;

    const skills = (profileSkillIds || []).map((id) => {
      return orgSkills.find((o: any) => String(o.id) === String(id));
    });

    setSkills(skills);
  }, [profileSkillIds, orgSkills]);

  useEffect(() => {
    if (profile) {
      setFirstNameInput(profile?.first_name || '');
      setLastNameInput(profile?.last_name || '');
      setAvatarUrl((profile?.image_url as string) || user?.imageUrl || null);
      const skillsFromApi = Array.isArray(profile?.skills) ? profile.skills.map((s: any) => String(s)) : [];
      setProfileSkillIds(skillsFromApi);
      setEnableUserMemoryStorage(profile?.enable_user_memory_storage || false);
      setReferenceUserChatHistory(profile?.reference_user_chat_history || false);
    }
  }, [profile, user]);

  return (
    <div className="bg-secondary-light dark:bg-secondary-dark rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
      <div className="px-6 py-5">
        {!isLoaded || loading ? (
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
            <div className="space-y-2 w-64">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 animate-pulse" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3 animate-pulse" />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 min-w-0">
              <button className="relative group cursor-pointer" onClick={handleAvatarClick}>
                <div className={`w-14 h-14 rounded-full border border-gray-200 dark:border-gray-700 overflow-hidden ${avatarUploading ? 'opacity-70' : ''}`}>
                  <img
                    src={avatarUrl || user?.imageUrl || ''}
                    alt="User avatar"
                    title="Change photo"
                    className={`w-full h-full object-cover rounded-full transition-opacity duration-150 group-hover:opacity-70`}
                  />
                </div>

                {!avatarUploading && (
                  <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center pointer-events-none">
                    <FaPen className="w-4 h-4 text-white drop-shadow" />
                  </div>
                )}

                {avatarUploading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin" />
                  </div>
                )}

                <input ref={fileInputRef} type="file" accept=".png, .jpg, .jpeg, .webp" className="hidden" onChange={handleAvatarFileChange} />
              </button>
              <div className="min-w-0 w-full">
                <div>
                  {!isEditingProfile ? (
                    <>
                      <div className="flex flex-row items-center text-base font-semibold text-gray-900 dark:text-white truncate">
                        <div>
                          {[firstNameInput, lastNameInput].filter(Boolean).join(' ') || '—'}
                        </div>
                        <div className="ml-2 text-gray-700 dark:text-gray-300 text-[10px] border rounded-lg px-2 py-1 leading-[10px] border-gray-600 dark:border-gray-500">
                          {roleLabel}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                        {user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || '—'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Joined {user?.createdAt ? new Date(user.createdAt as any).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 [&>*]:m-0">
                      <FormInput
                        id="first_name"
                        name="first_name"
                        label="First name"
                        value={firstNameInput}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
                          setFirstNameInput(val);
                        }}
                        placeholder="Enter first name"
                        className="text-sm h-9"
                      />
                      <FormInput
                        id="last_name"
                        name="last_name"
                        label="Last name"
                        value={lastNameInput}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
                          setLastNameInput(val);
                        }}
                        placeholder="Enter last name"
                        className="text-sm h-9"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditingProfile ? (
                <CustomButton variant="secondary" onClick={() => setIsEditingProfile(true)}>Edit</CustomButton>
              ) : (
                <div className="flex items-center gap-2">
                  <CustomButton variant="cancel" onClick={() => { 
                    setIsEditingProfile(false); 
                    setFirstNameInput(profile?.first_name || '');
                    setLastNameInput(profile?.last_name || '');
                  }}>
                    Cancel
                  </CustomButton>
                  <CustomButton variant="primary" onClick={handleSaveProfile}>
                    {updatingProfile && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    <div>
                      Save
                    </div>
                  </CustomButton>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Skills section - read-only */}
        <div className="mt-5">
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">Skills</div>
            {skills.length === 0 ? (
              <div className="text-sm text-gray-600 dark:text-gray-400">No skills</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {skills.map((s) => (
                  <span
                    key={s?.id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 flex-shrink-0"
                    title={s?.skill}
                  >
                    {s?.skill}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
};

export default UserProfile;